import { useEffect, useMemo, useState } from "react";
import { Banner, Button, Dialog, Field, Segmented, Select, TextArea, TextInput, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import { salesApi, type Currency, type DealChannel, type DealDetail, type DealInput, type SalesRegion } from "../../api/salesApi";
import { CURRENCY_SIGN, errorMessage, fmtIls, fmtMoney, fromMinor, toMinor, todayIso, withEmpty } from "./salesUi";
import { useSalesMeta } from "./useSalesMeta";

export type DealPrefill = { leadId: string; name: string | null; phone: string | null; productId: string | null; source: string | null; repId: string | null };

type Paid = "full" | "partial" | "none";

type Form = {
  name: string;
  phone: string;
  city: string;
  community: string;
  productId: string;
  productName: string;
  currency: Currency;
  fxRate: string;
  listPrice: string;
  discount: string;
  installments: string;
  paymentMethodKey: string;
  channel: DealChannel;
  repId: string;
  region: SalesRegion;
  dealOn: string;
  source: string;
  notes: string;
  paid: Paid;
  paidAmount: string;
};

/** «עסקה חדשה» / «עריכת עסקה» — customer, product & price (ILS / USD with a rate), payment, channel. */
export function DealDialog({
  open,
  deal,
  prefill,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** edit this deal (null = new) */
  deal?: DealDetail | null;
  /** closing a lead */
  prefill?: DealPrefill | null;
  onClose: () => void;
  onSaved: (d: DealDetail) => void;
}) {
  const toast = useCkToast();
  const { meta, optionsOf, productOptions, repOptions } = useSalesMeta();
  const settings = meta?.settings;
  const [f, setF] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    const rate = String(settings?.defaultUsdRate ?? "");
    if (deal) {
      setF({
        name: deal.customerName ?? "",
        phone: deal.customerPhone ?? "",
        city: "",
        community: "",
        productId: deal.productId ?? "",
        productName: deal.productName,
        currency: deal.currency,
        fxRate: deal.fxRate ? String(deal.fxRate) : rate,
        listPrice: fromMinor(deal.listPriceAgorot),
        discount: fromMinor(deal.discountAgorot),
        installments: String(deal.installments || 1),
        paymentMethodKey: deal.paymentMethodKey ?? "",
        channel: deal.channel,
        repId: deal.repId ?? "",
        region: deal.region,
        dealOn: deal.dealOn,
        source: deal.source ?? "",
        notes: deal.notes ?? "",
        paid: "none",
        paidAmount: "",
      });
      return;
    }
    const product = prefill?.productId ? meta?.products.find((p) => p.id === prefill.productId) : undefined;
    const currency = product?.currency ?? "ILS";
    setF({
      name: prefill?.name ?? "",
      phone: prefill?.phone ?? "",
      city: "",
      community: "",
      productId: product?.id ?? "",
      productName: product?.name ?? "",
      currency,
      fxRate: rate,
      listPrice: fromMinor(product?.priceAgorot),
      discount: "",
      installments: "1",
      paymentMethodKey: "",
      channel: "rep",
      repId: prefill?.repId ?? "",
      region: currency === "ILS" ? "domestic" : "abroad",
      dealOn: todayIso(),
      source: prefill?.source ?? "",
      notes: "",
      paid: "none",
      paidAmount: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deal, prefill]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((p) => (p ? { ...p, [k]: v } : p));

  const pickProduct = (id: string) => {
    const p = meta?.products.find((x) => x.id === id);
    setF((prev) =>
      prev
        ? {
            ...prev,
            productId: id,
            productName: p?.name ?? prev.productName,
            currency: p?.currency ?? prev.currency,
            listPrice: p ? fromMinor(p.priceAgorot) : prev.listPrice,
            region: p ? (p.currency === "ILS" ? "domestic" : "abroad") : prev.region,
          }
        : prev,
    );
  };

  const calc = useMemo(() => {
    if (!f) return null;
    const list = toMinor(f.listPrice) ?? 0;
    const disc = toMinor(f.discount) ?? 0;
    const rate = f.currency === "ILS" ? 1 : Number(f.fxRate) || 0;
    const total = Math.round((list - disc) * rate);
    const vatApplies =
      Boolean(settings) &&
      (settings?.vatPercent ?? 0) > 0 &&
      (!settings?.vatDomesticOnly || f.region === "domestic") &&
      (!settings?.vatPaymentMethods.length || settings.vatPaymentMethods.includes(f.paymentMethodKey));
    const vat = vatApplies && settings ? Math.round((total * settings.vatPercent) / (100 + settings.vatPercent)) : 0;
    return { list, disc, rate, total, vatApplies, vat };
  }, [f, settings]);

  if (!f || !calc) return null;
  const isNew = !deal;
  const foreign = f.currency !== "ILS";

  const submit = async () => {
    setErr(null);
    if (!f.productId && !f.productName.trim()) return setErr("צריך לבחור מוצר");
    if (isNew && !prefill && !f.name.trim() && !f.phone.trim()) return setErr("צריך שם או טלפון של הלקוח");
    if (foreign && !(Number(f.fxRate) > 0)) return setErr("חסר שער המרה לשקלים");
    if (calc.total < 0) return setErr("ההנחה גדולה מהמחיר");
    const body: DealInput = {
      productId: f.productId || null,
      productName: f.productId ? null : f.productName.trim() || null,
      currency: f.currency,
      listPriceAgorot: calc.list,
      discountAgorot: calc.disc,
      fxRate: foreign ? Number(f.fxRate) : null,
      installments: Math.min(36, Math.max(1, Math.round(Number(f.installments) || 1))),
      paymentMethodKey: f.paymentMethodKey || null,
      channel: f.channel,
      repId: f.channel === "rep" ? f.repId || null : null,
      region: f.region,
      dealOn: f.dealOn || todayIso(),
      source: f.source || null,
      notes: f.notes.trim() || null,
    };
    if (isNew) {
      if (prefill) body.leadId = prefill.leadId;
      if (!prefill || f.name !== (prefill.name ?? "") || f.phone !== (prefill.phone ?? "") || f.city || f.community) {
        body.customer = { name: f.name.trim() || null, phone: f.phone.trim() || null, city: f.city.trim() || null, community: f.community.trim() || null };
      }
      if (f.paid === "full") body.paidInFull = true;
      if (f.paid === "partial") {
        const amt = toMinor(f.paidAmount);
        if (!amt || amt <= 0) return setErr("סכום התשלום לא תקין");
        body.payment = { amountAgorot: amt, paidOn: body.dealOn, methodKey: body.paymentMethodKey };
      }
    }
    setBusy(true);
    try {
      const saved = isNew ? await salesApi.deals.create(body) : await salesApi.deals.update(deal.id, body);
      toast.success(isNew ? "העסקה נשמרה" : "העסקה עודכנה");
      onSaved(saved);
      onClose();
    } catch (e) {
      setErr(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  const sign = CURRENCY_SIGN[f.currency];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      icon="receipt"
      title={isNew ? (prefill ? "סגירת עסקה" : "עסקה חדשה") : "עריכת עסקה"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" icon="check" disabled={busy} onClick={() => void submit()}>
            {busy ? "שומר…" : "שמירה"}
          </Button>
        </>
      }
    >
      <div className="sl-stack">
        {err ? <Banner tone="danger">{err}</Banner> : null}

        <h4 className="sl-section-title">לקוח</h4>
        {isNew ? (
          <div className="sl-grid2">
            <Field label="שם">
              <TextInput value={f.name} onChange={(e) => set("name", e.target.value)} autoFocus={!prefill} />
            </Field>
            <Field label="טלפון" hint={prefill ? "מהליד — אפשר לתקן" : "לקוח קיים עם אותו טלפון יקושר אליו"}>
              <TextInput ltr type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
            <Field label="עיר">
              <TextInput value={f.city} onChange={(e) => set("city", e.target.value)} />
            </Field>
            <Field label="קהילה / חסידות">
              <TextInput value={f.community} onChange={(e) => set("community", e.target.value)} />
            </Field>
          </div>
        ) : (
          <p className="sl-note">
            <b>{f.name || "—"}</b> {f.phone ? <span className="sl-ltr">{f.phone}</span> : null}
          </p>
        )}

        <h4 className="sl-section-title">מוצר ומחיר</h4>
        <div className="sl-grid2">
          <Field label="קורס / מוצר">
            <Select options={withEmpty("— בחירה —", productOptions)} value={f.productId} onChange={pickProduct} aria-label="מוצר" />
          </Field>
          {!f.productId ? (
            <Field label="שם המוצר" hint="כשאין מוצר ברשימה">
              <TextInput value={f.productName} onChange={(e) => set("productName", e.target.value)} />
            </Field>
          ) : null}
          <Field label="מטבע">
            <Segmented
              ariaLabel="מטבע"
              value={f.currency === "USD" ? "USD" : "ILS"}
              onChange={(v) => {
                set("currency", v as Currency);
                set("region", v === "ILS" ? "domestic" : "abroad");
              }}
              items={[
                { value: "ILS", label: "₪ שקל" },
                { value: "USD", label: "$ דולר" },
              ]}
            />
          </Field>
          {foreign ? (
            <Field label="שער המרה" hint={`ברירת המחדל: ${settings?.defaultUsdRate ?? "—"}`}>
              <TextInput ltr inputMode="decimal" value={f.fxRate} onChange={(e) => set("fxRate", e.target.value)} />
            </Field>
          ) : null}
          <Field label={`מחיר מחירון (${sign})`}>
            <TextInput ltr inputMode="decimal" value={f.listPrice} onChange={(e) => set("listPrice", e.target.value)} />
          </Field>
          <Field label={`הנחה (${sign})`}>
            <TextInput ltr inputMode="decimal" value={f.discount} onChange={(e) => set("discount", e.target.value)} />
          </Field>
        </div>
        <div className="sl-total">
          <span>סה״כ לעסקה</span>
          <b>{fmtIls(calc.total)}</b>
          {foreign ? <span className="sl-muted">({fmtMoney(calc.list - calc.disc, f.currency)} × {calc.rate || "?"})</span> : null}
          {calc.vatApplies ? <span className="sl-muted">· כולל מע״מ {settings?.vatPercent}%: {fmtIls(calc.vat)}</span> : <span className="sl-muted">· ללא מע״מ</span>}
        </div>

        <h4 className="sl-section-title">תשלום ומכירה</h4>
        <div className="sl-grid2">
          <Field label="צורת תשלום">
            <Select options={withEmpty("—", optionsOf("sales_payment_method"))} value={f.paymentMethodKey} onChange={(v) => set("paymentMethodKey", v)} aria-label="צורת תשלום" />
          </Field>
          <Field label="תשלומים">
            <Select
              aria-label="תשלומים"
              value={f.installments}
              onChange={(v) => set("installments", v)}
              options={Array.from({ length: 36 }, (_, i) => ({ value: String(i + 1), label: i ? `${i + 1} תשלומים` : "תשלום אחד" }))}
            />
          </Field>
          <Field label="אזור">
            <Segmented
              ariaLabel="אזור"
              value={f.region}
              onChange={(v) => set("region", v as SalesRegion)}
              items={[
                { value: "domestic", label: "ישראל" },
                { value: "abroad", label: "חו״ל" },
              ]}
            />
          </Field>
          <Field label="תאריך העסקה">
            <TextInput type="date" value={f.dealOn} onChange={(e) => set("dealOn", e.target.value)} />
          </Field>
          <Field label="ערוץ">
            <Segmented
              ariaLabel="ערוץ"
              value={f.channel}
              onChange={(v) => set("channel", v as DealChannel)}
              items={[
                { value: "rep", label: "נציג" },
                { value: "self_service", label: "דרך הקו" },
                { value: "partial", label: "עסקה חלקית" },
              ]}
            />
          </Field>
          {f.channel === "rep" ? (
            <Field label="נציג">
              <Select options={withEmpty("— ללא —", repOptions)} value={f.repId} onChange={(v) => set("repId", v)} aria-label="נציג" />
            </Field>
          ) : null}
          <Field label="מקור">
            <Select options={withEmpty("—", optionsOf("sales_lead_source"))} value={f.source} onChange={(v) => set("source", v)} aria-label="מקור" />
          </Field>
        </div>
        <Field label="הערות" full>
          <TextArea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>

        {isNew ? (
          <>
            <h4 className="sl-section-title">שולם עכשיו?</h4>
            <div className="sl-row">
              <Segmented
                ariaLabel="שולם עכשיו"
                value={f.paid}
                onChange={(v) => set("paid", v as Paid)}
                items={[
                  { value: "none", label: "לא שולם" },
                  { value: "partial", label: "סכום חלקי" },
                  { value: "full", label: "שולם במלואו" },
                ]}
              />
              {f.paid === "partial" ? (
                <TextInput ltr inputMode="decimal" aria-label="סכום ששולם (₪)" placeholder="סכום ב־₪" value={f.paidAmount} onChange={(e) => set("paidAmount", e.target.value)} style={{ width: 140 }} />
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
